import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import useRefund from '../../jetSetter/hooks/useRefund';
import RefundApprovalModal from '../components/RefundApprovalModal';

// Helper function to format dates
const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  } catch (e) {
    return 'Invalid Date';
  }
};

const RefundHistoryPage = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(20);
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [selectedRefund, setSelectedRefund] = useState(null);
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null);
  const [sortOrder, setSortOrder] = useState('DESC');

  const { getRefundHistory, getRefundDetails, retryFailedRefundPayment } = useRefund();
  const [retryingId, setRetryingId] = useState(null);

  const loadRefundHistory = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getRefundHistory(currentPage, recordsPerPage, statusFilter, sortOrder);
      if (result) {
        setRefunds(result.refund_requests);
        setPagination(result.pagination);
      }
    } catch (error) {
      console.error('Error loading refund history:', error);
      toast.error('Failed to load refund history');
    } finally {
      setLoading(false);
    }
  }, [currentPage, recordsPerPage, statusFilter, sortOrder, getRefundHistory]);

  useEffect(() => {
    loadRefundHistory();
  }, [loadRefundHistory]);

  const handleOpenDetailsModal = async (refund) => {
    const details = await getRefundDetails(refund.id);
    if (details) {
      setSelectedRefund(details);
      setApprovalModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setApprovalModalOpen(false);
    setSelectedRefund(null);
  };

  const handleRetryPayment = async (refund) => {
    setRetryingId(refund.id);
    try {
      const result = await retryFailedRefundPayment(refund.id);
      if (result?.success) {
        toast.success(result.message || 'Refund payment completed');
        loadRefundHistory();
      } else {
        toast.error(result?.message || 'Refund payment retry failed');
      }
    } catch (err) {
      toast.error('Refund payment retry failed');
    } finally {
      setRetryingId(null);
    }
  };

  const handleRecordsPerPageChange = (e) => {
    setRecordsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value || null);
    setCurrentPage(1);
  };

  const handleSortOrderChange = (e) => {
    setSortOrder(e.target.value);
    setCurrentPage(1);
  };

  const handlePreviousPage = () => {
    setCurrentPage(Math.max(1, currentPage - 1));
  };

  const handleNextPage = () => {
    if (pagination) {
      setCurrentPage(Math.min(pagination.totalPages, currentPage + 1));
    }
  };

  const handlePageChange = (pageNum) => {
    setCurrentPage(pageNum);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Pending Approval':
        return 'bg-amber-100 text-amber-800';
      case 'Awaiting Admin Approval':
        return 'bg-blue-100 text-blue-800';
      case 'Processing':
        return 'bg-cyan-100 text-cyan-800';
      case 'Completed':
        return 'bg-green-100 text-green-800';
      case 'Rejected':
        return 'bg-red-100 text-red-800';
      case 'Failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const totalRecords = pagination?.total || 0;
  const totalPages = pagination?.totalPages || 1;
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + refunds.length;

  return (
    <div className="p-6 bg-white">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Refund Management</h1>
        <p className="text-gray-600 mt-2">Review and approve pending refund requests</p>
      </div>

      {/* Header and Controls */}
      <div className="flex mx-[15px] justify-between items-center py-[15px] px-[15px] md:px-[30px] bg-transparent mb-[15px] flex-col md:flex-row gap-[15px] md:gap-0 items-start md:items-center">
        <div className="flex items-center gap-2 text-[13px] text-[#555] w-full md:w-auto justify-between md:justify-start">
          <span>Display</span>
          <select
            value={recordsPerPage}
            onChange={handleRecordsPerPageChange}
            className="w-[75px] h-[34px] py-1.5 px-3 text-[13px] leading-[1.42857143] text-black bg-white border border-[#ccc] rounded-[5px] shadow-none font-semibold inline-block align-middle my-0 mx-[5px] transition-[border-color] duration-150 focus:outline-none focus:border-[#ccc] focus:shadow-none"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span>Records</span>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2.5 m-0 w-full md:w-auto">
          <div className="flex items-center gap-2.5 w-full sm:w-auto">
            <label className="text-[13px] text-[#555] font-normal">Status</label>
            <select
              value={statusFilter || ''}
              onChange={handleStatusFilterChange}
              className="inline-block w-full sm:w-[150px] h-[34px] py-1.5 px-3 text-[13px] leading-[1.42857143] text-[#4e4c4c] bg-white border border-[#ccc] rounded-[5px] shadow-none align-middle appearance-none box-border transition-[border-color,box-shadow] duration-150 font-normal focus:outline-none focus:border-[#ccc] focus:shadow-none"
            >
              <option value="">All Statuses</option>
              <option value="Pending Approval">Pending Approval</option>
              <option value="Awaiting Admin Approval">Awaiting Admin Approval</option>
              <option value="Processing">Processing</option>
              <option value="Completed">Completed</option>
              <option value="Rejected">Rejected</option>
              <option value="Failed">Failed</option>
            </select>
          </div>

          <div className="flex items-center gap-2.5 w-full sm:w-auto">
            <label className="text-[13px] text-[#555] font-normal">Sort</label>
            <select
              value={sortOrder}
              onChange={handleSortOrderChange}
              className="inline-block w-full sm:w-[150px] h-[34px] py-1.5 px-3 text-[13px] leading-[1.42857143] text-[#4e4c4c] bg-white border border-[#ccc] rounded-[5px] shadow-none align-middle appearance-none box-border transition-[border-color,box-shadow] duration-150 font-normal focus:outline-none focus:border-[#ccc] focus:shadow-none"
            >
              <option value="DESC">Newest First</option>
              <option value="ASC">Oldest First</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white mx-[15px] mt-0 mb-[15px] rounded-none shadow-none border border-[#ddd] overflow-x-auto max-w-full" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <table className="border-collapse min-w-[1000px] w-full">
          <thead className="bg-white border-b-0">
            <tr>
              <th className="px-3 py-3 border-2 border-gray-200 bg-white text-left text-xs font-semibold text-gray-600 tracking-wider" style={{ textTransform: 'none' }}>
                #
              </th>
              <th className="px-3 py-3 border-2 border-gray-200 bg-white text-left text-xs font-semibold text-gray-600 tracking-wider" style={{ textTransform: 'none' }}>
                Booking Ref
              </th>
              <th className="px-3 py-3 border-2 border-gray-200 bg-white text-left text-xs font-semibold text-gray-600 tracking-wider" style={{ textTransform: 'none' }}>
                Jetsetter
              </th>
              <th className="px-3 py-3 border-2 border-gray-200 bg-white text-left text-xs font-semibold text-gray-600 tracking-wider" style={{ textTransform: 'none' }}>
                Refund Amount
              </th>
              <th className="px-3 py-3 border-2 border-gray-200 bg-white text-left text-xs font-semibold text-gray-600 tracking-wider" style={{ textTransform: 'none' }}>
                Approvals
              </th>
              <th className="px-3 py-3 border-2 border-gray-200 bg-white text-left text-xs font-semibold text-gray-600 tracking-wider" style={{ textTransform: 'none' }}>
                Requested
              </th>
              <th className="px-3 py-3 border-2 border-gray-200 bg-white text-left text-xs font-semibold text-gray-600 tracking-wider" style={{ textTransform: 'none' }}>
                Completed
              </th>
              <th className="px-3 py-3 border-2 border-gray-200 bg-white text-left text-xs font-semibold text-gray-600 tracking-wider" style={{ textTransform: 'none' }}>
                Status
              </th>
              <th className="px-3 py-3 border-2 border-gray-200 bg-white text-left text-xs font-semibold text-gray-600 tracking-wider" style={{ textTransform: 'none' }}>
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} className="text-center p-5 text-[#999] text-[13px]">
                  Loading refund history...
                </td>
              </tr>
            ) : refunds.length === 0 ? (
              <tr>
                <td colSpan={9} className="text-center p-5 text-[#999] text-[13px]">
                  No refund records found
                </td>
              </tr>
            ) : (
              refunds.map((refund, index) => (
                <tr key={refund.id} className="hover:bg-white">
                  <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0">
                    {startIndex + index + 1}
                  </td>
                  <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0 font-semibold">
                    {refund.booking_reference}
                  </td>
                  <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0">
                    <div>{refund.user_name}</div>
                    <div className="text-xs text-gray-500">{refund.user_email}</div>
                  </td>
                  <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0 font-semibold">
                    ${parseFloat(refund.refund_amount).toFixed(2)}
                  </td>
                  <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0 font-semibold">
                    {refund.approvals_received}/{refund.approvals_required}
                  </td>
                  <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0">
                    {formatDateTime(refund.requested_at)}
                  </td>
                  <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0">
                    {refund.completed_at ? formatDateTime(refund.completed_at) : '-'}
                  </td>
                  <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusBadgeClass(refund.status)}`}>
                      {refund.status}
                    </span>
                  </td>
                  <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0 text-end">
                    <div className="flex justify-end gap-1">
                      {refund.status === 'Failed' && (
                        <button
                          onClick={() => handleRetryPayment(refund)}
                          disabled={retryingId === refund.id}
                          className="px-2 h-6 flex items-center justify-center bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors text-[10px] font-semibold disabled:opacity-50"
                          title="Retry Stripe refund"
                        >
                          {retryingId === refund.id ? (
                            <i className="fas fa-spinner fa-spin text-[9px]"></i>
                          ) : (
                            'Retry'
                          )}
                        </button>
                      )}
                      <button
                        onClick={() => handleOpenDetailsModal(refund)}
                        className="w-6 h-6 flex items-center justify-center bg-[#3b5998] text-white rounded hover:bg-[#2d4373] transition-colors"
                        title="View Details"
                      >
                        <i className="fas fa-eye text-[9px]"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex justify-between items-center mx-[15px] md:mx-[30px] my-0 py-[15px] px-0 bg-transparent gap-2 flex-nowrap">
        <div className="text-[13px] text-[#555] text-left whitespace-nowrap flex-shrink min-w-0 overflow-hidden text-ellipsis">
          Showing {totalRecords === 0 ? 0 : startIndex + 1} to {endIndex} of {totalRecords} entries
        </div>
        <div className="inline-flex flex-shrink-0 overflow-x-auto">
          <button
            onClick={handlePreviousPage}
            className="py-[5px] px-4 border border-[#ddd] bg-white rounded-tl-md rounded-bl-md rounded-tr-none rounded-br-none border-r-0 cursor-pointer text-base text-[#aaa] h-[34px] min-w-[80px] sm:min-w-[100px] font-light inline-block align-middle appearance-none box-border transition-all duration-200 leading-[1.5] text-center whitespace-nowrap select-none shadow-[0_1px_3px_rgba(0,0,0,0.05)] hover:bg-[#f9f9f9] hover:border-[#ccc] hover:text-[#888] focus:outline-none focus:border-[#ddd] focus:shadow-[0_1px_3px_rgba(0,0,0,0.05)]"
            disabled={currentPage === 1}
          >
            Previous
          </button>

          {/* Page Number Buttons */}
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
            <button
              key={pageNum}
              onClick={() => handlePageChange(pageNum)}
              className={`py-[5px] px-3 border border-[#ddd] cursor-pointer text-base h-[34px] min-w-[40px] font-light inline-block align-middle appearance-none box-border transition-all duration-200 leading-[1.5] text-center whitespace-nowrap select-none shadow-[0_1px_3px_rgba(0,0,0,0.05)] ${pageNum === currentPage
                  ? 'bg-[#ffeb00] text-black border-[#ffeb00] font-semibold'
                  : 'bg-white text-[#aaa] hover:bg-[#f9f9f9] hover:border-[#ccc] hover:text-[#888]'
                } focus:outline-none focus:border-[#ddd] focus:shadow-[0_1px_3px_rgba(0,0,0,0.05)]`}
            >
              {pageNum}
            </button>
          ))}

          <button
            onClick={handleNextPage}
            className="py-[5px] px-4 border border-[#ddd] bg-white rounded-tr-md rounded-br-md rounded-tl-none rounded-bl-none cursor-pointer text-base text-[#aaa] h-[34px] min-w-[80px] sm:min-w-[100px] font-light inline-block align-middle appearance-none box-border transition-all duration-200 leading-[1.5] text-center whitespace-nowrap select-none shadow-[0_1px_3px_rgba(0,0,0,0.05)] hover:bg-[#f9f9f9] hover:border-[#ccc] hover:text-[#888] focus:outline-none focus:border-[#ddd] focus:shadow-[0_1px_3px_rgba(0,0,0,0.05)]"
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>

      {/* Details Modal */}
      <RefundApprovalModal
        refund={selectedRefund}
        approvalType="view"
        isOpen={approvalModalOpen}
        onClose={handleCloseModal}
        onSuccess={handleCloseModal}
      />
    </div>
  );
};

export default RefundHistoryPage;
